import {
  BLOCK_EFFECTS,
  FORM_FIELD_TYPES,
  formCoverage,
  type FormTestInput,
  type InsurerForm,
  type ValidationIssue,
} from '@cyberscore/contracts';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  CheckCircle2,
  FileText,
  ListTree,
  Plus,
  ShieldCheck,
  Table2,
  Trash2,
  Users,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { href } from '../../lib/router';
import { useToast } from '../../ui/Toasts';
import { useQuestionInfo, useValidate } from './api';
import { useItemDraft } from './draft';
import { EditorHead, fromList, toList } from './EditorParts';
import { FormTestEditor, PdfTargetEditor, QUESTIONS_LIST, SourcesEditor } from './FormParts';
import {
  allFields,
  EFFECT_LABEL,
  FIELD_TYPE_LABEL,
  fieldKeyFor,
  fieldSourceText,
  formIssues,
  issueLabel,
  move,
  newField,
  optionsFor,
  parseScalar,
  pdfKind,
  removeField,
  renameField,
  sourceValue,
  type FieldDraft,
  type FormDraft,
} from './insurer-form';

type Sel = { section: number; field: number | null };

/**
 * An insurer's proposal form (task 96): its sections and fields, where each field's answer comes from in
 * an assessment, who the form is for, the answers that stop a quote, the insurer's premium table and where
 * each field goes on the insurer's own PDF. Saved as a draft and published by a release like any change.
 */
export function InsurerFormEditor({ id }: { id: string }) {
  const d = useItemDraft<FormDraft>(id, href.insurerForm);
  const info = useQuestionInfo();
  const questions = useMemo(() => new Map(info.map((q) => [q.key, q])), [info]);
  const validate = useValidate();
  const toast = useToast();
  const [sel, setSel] = useState<Sel>({ section: 0, field: null });
  const [server, setServer] = useState<ValidationIssue[] | null>(null);

  if (d.item.isPending || !d.draft) return <div className="empty">Loading…</div>;
  if (d.item.isError) return <div className="empty">{d.item.error.message}</div>;

  const form = d.draft;
  const off = !d.owns;
  const issues = formIssues(form);
  const set = (patch: Partial<FormDraft>) => d.setDraft((x) => ({ ...x, ...patch }));
  const setForm = (f: (x: FormDraft) => FormDraft) => d.setDraft(f);
  const si = Math.min(sel.section, form.sections.length - 1);
  const section = form.sections[si]!;
  const fi = sel.field !== null && sel.field < section.fields.length ? sel.field : null;
  const field = fi === null ? null : section.fields[fi]!;
  const setSection = (patch: Partial<FormDraft['sections'][number]>) =>
    setForm((x) => ({ ...x, sections: x.sections.map((s, i) => (i === si ? { ...s, ...patch } : s)) }));
  const setField = (patch: Partial<FieldDraft>) =>
    setForm((x) => ({ ...x, sections: x.sections.map((s, i) => (i === si ? { ...s, fields: s.fields.map((f, j) => (j === fi ? { ...f, ...patch } : f)) } : s)) })); // prettier-ignore
  const keys = new Set(allFields(form).map((x) => x.field.key));
  const coverage = formCoverage(form as unknown as InsurerForm);
  const onPdf = Object.keys(form.pdf?.fields ?? {}).length;

  const check = async () => {
    try {
      const r = await validate.mutateAsync();
      setServer(r.issues.filter((i) => i.kind === 'insurer_form' && i.key === d.item.data!.key));
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not check', 'bad');
    }
  };

  return (
    <>
      <EditorHead d={d} section="Insurer forms" sectionHref={href.studio()} title={`${form.insurer}: ${form.product}`} blocked={issues.length > 0} />
      <datalist id={QUESTIONS_LIST}>
        {info.map((q) => <option key={q.key} value={q.key}>{q.text}</option>)}
      </datalist>
      <div className="builder form-builder">
        <div className="stack">
          <section className="card pad" aria-labelledby="if-form">
            <h2 id="if-form" className="section-title row gap"><FileText size={16} /> The form</h2>
            <div className="two">
              <div className="field">
                <label htmlFor="if-insurer">Insurer</label>
                <input id="if-insurer" className="input" disabled={off} value={form.insurer} onChange={(e) => set({ insurer: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor="if-product">Product</label>
                <input id="if-product" className="input" disabled={off} value={form.product} onChange={(e) => set({ product: e.target.value })} />
              </div>
            </div>
            <div className="three">
              <div className="field">
                <label htmlFor="if-code">Form number and edition</label>
                <input id="if-code" className="input mono" disabled={off} value={form.form_code} onChange={(e) => set({ form_code: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor="if-country">Country</label>
                <input id="if-country" className="input mono" maxLength={2} disabled={off} value={form.country ?? 'MY'} onChange={(e) => set({ country: e.target.value.toUpperCase() })} />
              </div>
              <div className="field">
                <label htmlFor="if-doc">Blank form (file name)</label>
                <input id="if-doc" className="input" disabled={off} value={form.source_document ?? ''} onChange={(e) => set({ source_document: e.target.value || undefined })} />
              </div>
            </div>
            <div className="field">
              <label htmlFor="if-desc">Description</label>
              <textarea id="if-desc" className="input" rows={2} disabled={off} value={form.description ?? ''} onChange={(e) => set({ description: e.target.value })} />
            </div>
          </section>

          <Eligibility form={form} set={set} off={off} />

          <section className="card pad" aria-labelledby="if-sections">
            <h2 id="if-sections" className="section-title row gap"><ListTree size={16} /> Sections and fields</h2>
            <p className="muted small">The insurer’s sections in the form’s order. Select a field to say where its answer comes from.</p>
            <div className="section-tabs" role="tablist" aria-label="Sections">
              {form.sections.map((s, i) => (
                <button key={i} type="button" role="tab" aria-selected={i === si} className={`section-tab${i === si ? ' on' : ''}`} onClick={() => setSel({ section: i, field: null })}>
                  <span className="clamp-1">{s.title || s.key}</span>
                  <span className="muted small">{s.fields.length}</span>
                </button>
              ))}
              {!off && (
                <button type="button" className="section-tab add" onClick={() => { setForm((x) => ({ ...x, sections: [...x.sections, { key: `section_${x.sections.length + 1}`, title: 'New section', fields: [newField(keys)] }] })); setSel({ section: form.sections.length, field: 0 }); }}>
                  <Plus size={14} /> Section
                </button>
              )}
            </div>

            <div className="section-body" role="tabpanel" aria-label={`Section ${section.title || section.key}`}>
              <div className="three">
                <div className="field">
                  <label htmlFor="if-s-title">Title</label>
                  <input id="if-s-title" className="input" disabled={off} value={section.title} onChange={(e) => setSection({ title: e.target.value })} />
                </div>
                <div className="field">
                  <label htmlFor="if-s-key">Key</label>
                  <input id="if-s-key" className="input mono" disabled={off} value={section.key} onChange={(e) => setSection({ key: e.target.value })} />
                </div>
                {!off && (
                  <div className="row gap section-moves">
                    <button type="button" className="btn btn-icon btn-ghost" aria-label="Move section earlier" disabled={si === 0} onClick={() => { setForm((x) => ({ ...x, sections: move(x.sections, si, -1) })); setSel({ section: si - 1, field: null }); }}><ArrowUp size={16} /></button>
                    <button type="button" className="btn btn-icon btn-ghost" aria-label="Move section later" disabled={si === form.sections.length - 1} onClick={() => { setForm((x) => ({ ...x, sections: move(x.sections, si, 1) })); setSel({ section: si + 1, field: null }); }}><ArrowDown size={16} /></button>
                    <button type="button" className="btn btn-icon btn-ghost" aria-label="Remove section" disabled={form.sections.length === 1} onClick={() => { setForm((x) => { let next = x; for (let j = x.sections[si]!.fields.length - 1; j >= 0; j--) next = removeField(next, si, j); return { ...next, sections: next.sections.filter((_, i) => i !== si) }; }); setSel({ section: Math.max(0, si - 1), field: null }); }}><Trash2 size={16} /></button>
                  </div>
                )}
              </div>
              <div className="field">
                <span className="field-label">Only for some businesses</span>
                <span className="hint">A supplement the client completes only when one of these holds (OT, retail, biometrics…). Empty: always.</span>
                {(section.applies ?? []).map((t, i) => (
                  <div key={i} className="ft-item">
                    <FormTestEditor test={t} onChange={(x) => setSection({ applies: (section.applies ?? []).map((y, n) => (n === i ? x : y)) })} questions={questions} disabled={off} />
                    {!off && (
                      <button type="button" className="btn btn-icon btn-ghost" aria-label={`Remove condition ${i + 1}`} onClick={() => setSection({ applies: (section.applies ?? []).filter((_, n) => n !== i).length ? (section.applies ?? []).filter((_, n) => n !== i) : undefined })}><Trash2 size={14} /></button>
                    )}
                  </div>
                ))}
                {!off && (section.applies?.length ?? 0) < 5 && (
                  <button type="button" className="btn btn-sm" onClick={() => setSection({ applies: [...(section.applies ?? []), { from: 'answer', question: '', op: 'eq', value: 'yes' }] })}>
                    <Plus size={14} /> Add a condition
                  </button>
                )}
              </div>

              <div className="table-wrap">
                <table className="grid form-fields" aria-label="Fields">
                  <thead><tr><th>Ref</th><th>Field</th><th>Type</th><th>Answer from</th><th /></tr></thead>
                  <tbody>
                    {section.fields.map((f, j) => (
                      <tr key={j} className={`clickable${j === fi ? ' selected' : ''}`} onClick={() => setSel({ section: si, field: j })} aria-selected={j === fi}>
                        <td className="mono small">{f.ref}</td>
                        <td>
                          <button type="button" className="linklike left" onClick={(e) => { e.stopPropagation(); setSel({ section: si, field: j }); }}>{f.label || f.key}</button>
                          <div className="mono small muted">{f.key}</div>
                        </td>
                        <td className="small">{FIELD_TYPE_LABEL[f.type] ?? f.type}</td>
                        <td className="small">
                          {fieldSourceText(f)}
                          <div className="row gap wrap mt-xs">
                            {f.blocks && <span className="pill warn-pill">{f.blocks.effect}</span>}
                            {form.pdf?.fields[f.key] !== undefined && <span className="pill neutral">PDF</span>}
                            {f.required === false && <span className="pill neutral">optional</span>}
                          </div>
                        </td>
                        <td className="num nowrap">
                          {!off && (
                            <>
                              <button type="button" className="btn btn-icon btn-ghost" aria-label={`Move ${f.key} up`} disabled={j === 0} onClick={(e) => { e.stopPropagation(); setSection({ fields: move(section.fields, j, -1) }); setSel({ section: si, field: j - 1 }); }}><ArrowUp size={14} /></button>
                              <button type="button" className="btn btn-icon btn-ghost" aria-label={`Move ${f.key} down`} disabled={j === section.fields.length - 1} onClick={(e) => { e.stopPropagation(); setSection({ fields: move(section.fields, j, 1) }); setSel({ section: si, field: j + 1 }); }}><ArrowDown size={14} /></button>
                              <button type="button" className="btn btn-icon btn-ghost" aria-label={`Remove ${f.key}`} disabled={section.fields.length === 1} onClick={(e) => { e.stopPropagation(); setForm((x) => removeField(x, si, j)); setSel({ section: si, field: null }); }}><Trash2 size={14} /></button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!off && (
                <button type="button" className="btn mt" onClick={() => { setSection({ fields: [...section.fields, newField(keys)] }); setSel({ section: si, field: section.fields.length }); }}>
                  <Plus size={16} /> Add a field
                </button>
              )}
            </div>
          </section>

          {field && fi !== null && (
            <FieldEditor
              key={`${si}:${fi}`}
              form={form}
              field={field}
              off={off}
              questions={questions}
              onField={setField}
              onRename={(to) => setForm((x) => renameField(x, field.key, to))}
              taken={new Set([...keys].filter((k) => k !== field.key))}
              onPdf={(t) => setForm((x) => (x.pdf ? { ...x, pdf: { ...x.pdf, fields: Object.fromEntries([...Object.entries(x.pdf.fields).filter(([k]) => k !== field.key), ...(t === undefined ? [] : [[field.key, t] as const])]) } } : x))}
              onClose={() => setSel({ section: si, field: null })}
            />
          )}

          <Rating form={form} set={set} off={off} />
          <Pdf form={form} set={set} off={off} />
        </div>

        <aside className="stack form-aside">
          <section className={`card pad ${issues.length ? 'problems' : ''}`} aria-live="polite">
            {issues.length ? (
              <>
                <div className="eyebrow row gap"><AlertTriangle size={14} className="warn-t" /> To fix before saving ({issues.length})</div>
                <ul className="problem-list">
                  {issues.slice(0, 30).map((p, i) => (
                    <li key={i}>
                      {p.at ? (
                        <button type="button" className="linklike left" onClick={() => setSel({ section: p.at!.section, field: p.at!.field ?? null })}><b>{issueLabel(form, p)}:</b></button>
                      ) : (
                        <b>{issueLabel(form, p)}:</b>
                      )}{' '}
                      {p.message}
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <div className="ok-line"><CheckCircle2 size={16} className="ok-t" /> Ready to save.</div>
            )}
          </section>

          <section className="card pad" aria-label="Coverage">
            <div className="eyebrow">How much the assessment fills</div>
            <div className="cov-big">{Math.round(coverage.share * 100)}%</div>
            <div className="cov-bar" aria-hidden="true"><span style={{ width: `${coverage.share * 100}%` }} /></div>
            <dl className="cov-list small">
              <dt>From the assessment</dt><dd>{coverage.assessment}</dd>
              <dt>Director declares</dt><dd>{coverage.declaration}</dd>
              <dt>Broker fills</dt><dd>{coverage.broker}</dd>
              <dt>Doesn’t apply</dt><dd>{coverage.not_applicable}</dd>
              <dt>Not mapped</dt><dd>{coverage.unmapped}</dd>
              <dt>On the PDF</dt><dd>{onPdf} of {coverage.total}</dd>
            </dl>
          </section>

          <section className="card pad">
            <button type="button" className="btn" onClick={check} disabled={validate.isPending}>
              <ShieldCheck size={16} /> {validate.isPending ? 'Checking…' : 'Check against the questions'}
            </button>
            <p className="hint mt-xs">That every question and control it reads exists, with the values it compares.</p>
            {server && (server.length ? (
              <ul className="problem-list">{server.map((i, n) => <li key={n}>{i.message}</li>)}</ul>
            ) : (
              <div className="ok-line"><CheckCircle2 size={16} className="ok-t" /> Every reference exists.</div>
            ))}
          </section>

          <section className="card pad">
            <div className="eyebrow">Applies to</div>
            <p className="small">Assessments that prepare <b className="mono">{d.item.data!.key}</b>, from the release that publishes it. The forms question offers it, and the questions its fields read are asked when it is chosen.</p>
          </section>
        </aside>
      </div>
    </>
  ); // prettier-ignore
}

/** Who the form is for: a revenue band, and yes/no fields that must be "Yes". */
function Eligibility({
  form,
  set,
  off,
}: {
  form: FormDraft;
  set: (p: Partial<FormDraft>) => void;
  off: boolean;
}) {
  const e = form.eligibility;
  const yesNo = allFields(form)
    .map((x) => x.field)
    .filter((f) => f.type === 'yes_no');
  const rev = e?.revenue ?? {};
  const setRev = (k: 'min' | 'max' | 'below', v: string) => {
    const next = { ...rev, [k]: v === '' ? undefined : Number(v) };
    const any = next.min !== undefined || next.max !== undefined || next.below !== undefined;
    set({ eligibility: { fields: e?.fields ?? [], ...(any ? { revenue: next } : {}) } });
  };
  return (
    <section className="card pad" aria-labelledby="if-who">
      <h2 id="if-who" className="section-title row gap"><Users size={16} /> Who it is for</h2>
      <p className="muted small">A client outside this is proposed another form, or referred to the insurer instead of bound on this one.</p>
      {!e ? (
        <>
          <div className="empty compact">Any client.</div>
          {!off && <button type="button" className="btn btn-sm mt" onClick={() => set({ eligibility: { revenue: {}, fields: [] } })}><Plus size={14} /> Limit who it is for</button>}
        </>
      ) : (
        <>
          <div className="three">
            <div className="field">
              <label htmlFor="if-rev-min">Revenue at least (RM)</label>
              <input id="if-rev-min" className="input num" type="number" min={0} disabled={off} value={rev.min ?? ''} onChange={(x) => setRev('min', x.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="if-rev-max">at most (RM)</label>
              <input id="if-rev-max" className="input num" type="number" min={0} disabled={off} value={rev.max ?? ''} onChange={(x) => setRev('max', x.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="if-rev-below">or under (RM)</label>
              <input id="if-rev-below" className="input num" type="number" min={0} disabled={off} value={rev.below ?? ''} onChange={(x) => setRev('below', x.target.value)} />
            </div>
          </div>
          <div className="field">
            <span className="field-label">These must be “Yes”</span>
            <div className="check-grid">
              {yesNo.map((f) => (
                <label key={f.key} className="toggle-row small">
                  <input type="checkbox" disabled={off} checked={(e.fields ?? []).includes(f.key)} onChange={(x) => set({ eligibility: { ...e, fields: x.target.checked ? [...(e.fields ?? []), f.key] : (e.fields ?? []).filter((k) => k !== f.key) } })} />
                  <span><span className="mono">{f.ref}</span> {f.label}</span>
                </label>
              ))}
              {!yesNo.length && <span className="hint">The form has no yes/no fields.</span>}
            </div>
          </div>
          {!off && <button type="button" className="btn btn-sm" onClick={() => set({ eligibility: undefined })}><Trash2 size={14} /> Any client</button>}
        </>
      )}
    </section>
  ); // prettier-ignore
}

/** One field: what it asks, where the answer comes from, when it applies, what it blocks, where it goes on the PDF. */
function FieldEditor({
  form,
  field,
  off,
  questions,
  onField,
  onRename,
  taken,
  onPdf,
  onClose,
}: {
  form: FormDraft;
  field: FieldDraft;
  off: boolean;
  questions: ReturnType<typeof useQuestionInfo> extends (infer Q)[] ? Map<string, Q> : never;
  onField: (p: Partial<FieldDraft>) => void;
  onRename: (to: string) => void;
  /** Every other field's key, for the key a new field takes from its label. */
  taken: ReadonlySet<string>;
  onPdf: (t: Parameters<typeof PdfTargetEditor>[0]['target']) => void;
  onClose: () => void;
}) {
  const options = field.options ?? [];
  const checklist = Array.isArray(field.source) ? field.source.some((s) => s.from === 'checklist') : field.source.from === 'checklist'; // prettier-ignore
  const parents = allFields(form)
    .map((x) => x.field)
    .filter((f) => f.key !== field.key && (f.options?.length ?? 0) > 0);
  const parent = field.if ? parents.find((p) => p.key === field.if!.field) : undefined;
  const setOption = (i: number, patch: Partial<(typeof options)[number]>) =>
    onField({ options: options.map((o, n) => (n === i ? { ...o, ...patch } : o)) });
  const blockMode = field.blocks ? (field.blocks.lacks ? 'lacks' : 'in') : '';
  const blockValues = field.blocks?.in ?? field.blocks?.lacks ?? [];

  return (
    <section className="card pad field-editor" aria-label={`Field ${field.key}`}>
      <div className="row-between">
        <div className="eyebrow">Field {field.ref ? `${field.ref} · ` : ''}{field.key}</div>
        <button type="button" className="btn btn-sm btn-ghost" onClick={onClose}>Close</button>
      </div>
      <fieldset disabled={off} className="plain stack-sm">
        <div className="field">
          <label htmlFor="if-f-label">Label, as the insurer asks it</label>
          <textarea id="if-f-label" className="input" rows={2} value={field.label} onChange={(e) => { onField({ label: e.target.value }); if (/^new_field(_\d+)?$/.test(field.key) || field.key === fieldKeyFor(field.label, taken)) onRename(fieldKeyFor(e.target.value, taken)); }} />
        </div>
        <div className="three">
          <div className="field">
            <label htmlFor="if-f-ref">Insurer’s number</label>
            <input id="if-f-ref" className="input mono" value={field.ref} placeholder="e.g. B.4" onChange={(e) => onField({ ref: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="if-f-key">Key</label>
            <input id="if-f-key" className="input mono" value={field.key} onChange={(e) => onRename(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="if-f-type">Type</label>
            <select id="if-f-type" className="input" value={field.type} onChange={(e) => onField({ type: e.target.value as FieldDraft['type'], options: optionsFor(e.target.value, field.options) })}>
              {FORM_FIELD_TYPES.map((t) => <option key={t} value={t}>{FIELD_TYPE_LABEL[t]}</option>)}
            </select>
          </div>
        </div>
        <label className="toggle-row">
          <input type="checkbox" checked={field.required ?? true} onChange={(e) => onField({ required: e.target.checked })} /> Required on this form
        </label>

        {(options.length > 0 || field.type === 'choice' || field.type === 'multi_choice') && (
          <div className="field">
            <span className="field-label">Options</span>
            <ol className="option-list" aria-label="Options">
              {options.map((o, i) => (
                <li key={i} className="option-row">
                  <div className="ft-row">
                    <input className="input mono ft-path" aria-label={`Option ${i + 1} value`} value={o.value} onChange={(e) => setOption(i, { value: e.target.value })} />
                    <input className="input" aria-label={`Option ${i + 1} label`} value={o.label} onChange={(e) => setOption(i, { label: e.target.value })} />
                    {!off && (
                      <>
                        <button type="button" className="btn btn-icon btn-ghost" aria-label={`Move option ${i + 1} up`} disabled={i === 0} onClick={() => onField({ options: move(options, i, -1) })}><ArrowUp size={14} /></button>
                        <button type="button" className="btn btn-icon btn-ghost" aria-label={`Remove option ${i + 1}`} onClick={() => onField({ options: options.filter((_, n) => n !== i) })}><Trash2 size={14} /></button>
                      </>
                    )}
                  </div>
                  {checklist && (
                    <div className="ft-nested">
                      {o.test ? (
                        <div className="ft-item">
                          <FormTestEditor test={o.test} onChange={(t) => setOption(i, { test: t })} questions={questions} disabled={off} />
                          {!off && <button type="button" className="btn btn-icon btn-ghost" aria-label={`Leave option ${i + 1} to the client`} onClick={() => setOption(i, { test: undefined })}><Trash2 size={14} /></button>}
                        </div>
                      ) : (
                        <span className="hint">
                          Left for the client to tick.{' '}
                          {!off && <button type="button" className="linklike" onClick={() => setOption(i, { test: { from: 'control', controls: [] } as FormTestInput })}>Tick it by a test</button>}
                        </span>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ol>
            {!off && (
              <button type="button" className="btn btn-sm" onClick={() => onField({ options: [...options, { value: `option_${options.length + 1}`, label: `Option ${options.length + 1}` }] })}>
                <Plus size={14} /> Add an option
              </button>
            )}
          </div>
        )}
      </fieldset>

      <div className="field mt">
        <span className="field-label">Where the answer comes from</span>
        <span className="hint">The first source that gives an answer is used. A test answers a yes/no field: yes, no, or not known yet.</span>
        <SourcesEditor field={field} onChange={(list) => onField({ source: sourceValue(list) })} questions={questions} disabled={off} />
      </div>

      <fieldset disabled={off} className="plain stack-sm mt">
        <div className="field">
          <span className="field-label">Asked only if</span>
          <div className="ft-row">
            <select className="input" aria-label="Another field" value={field.if?.field ?? ''} onChange={(e) => onField({ if: e.target.value ? { field: e.target.value, in: [parents.find((p) => p.key === e.target.value)!.options![0]!.value] } : undefined })}>
              <option value="">Always</option>
              {parents.map((p) => <option key={p.key} value={p.key}>{p.ref ? `${p.ref} · ` : ''}{p.label}</option>)}
            </select>
          </div>
          {parent && (
            <div className="row gap wrap">
              <span className="small muted">is</span>
              {parent.options!.map((o) => (
                <label key={o.value} className="toggle-row small">
                  <input type="checkbox" checked={field.if!.in.map(String).includes(o.value)} onChange={(e) => { const next = e.target.checked ? [...field.if!.in, o.value] : field.if!.in.filter((v) => String(v) !== o.value); if (next.length) onField({ if: { ...field.if!, in: next } }); }} /> {o.label}
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="field">
          <span className="field-label">Doesn’t apply to the business when</span>
          {field.na_when ? (
            <div className="ft-item">
              <FormTestEditor test={field.na_when} onChange={(t) => onField({ na_when: t })} questions={questions} disabled={off} />
              {!off && <button type="button" className="btn btn-icon btn-ghost" aria-label="Always applies" onClick={() => onField({ na_when: undefined })}><Trash2 size={14} /></button>}
            </div>
          ) : (
            <span className="hint">
              Always applies.{' '}
              {!off && <button type="button" className="linklike" onClick={() => onField({ na_when: { from: 'answer', question: '', op: 'eq', value: 'no' } })}>Add a condition</button>}
            </span>
          )}
        </div>

        <div className="field">
          <span className="field-label">Stops or changes a quote</span>
          <div className="ft-row">
            <select className="input" aria-label="Blocks when" value={blockMode} onChange={(e) => onField({ blocks: e.target.value === '' ? undefined : { effect: field.blocks?.effect ?? 'refer', note: field.blocks?.note ?? 'Explain what happens, for the broker.', [e.target.value]: blockValues.length ? blockValues : [options[options.length - 1]?.value ?? 'no'] } })}>
              <option value="">Never</option>
              <option value="in">When the answer is one of</option>
              {field.type === 'multi_choice' && <option value="lacks">When none of these is ticked</option>}
            </select>
            {field.blocks && (
              <select className="input" aria-label="Effect" value={field.blocks.effect} onChange={(e) => onField({ blocks: { ...field.blocks!, effect: e.target.value as (typeof BLOCK_EFFECTS)[number] } })}>
                {BLOCK_EFFECTS.map((x) => <option key={x} value={x}>{EFFECT_LABEL[x]}</option>)}
              </select>
            )}
          </div>
          {field.blocks && (
            <>
              {options.length ? (
                <div className="row gap wrap">
                  {options.map((o) => (
                    <label key={o.value} className="toggle-row small">
                      <input type="checkbox" checked={blockValues.map(String).includes(o.value)} onChange={(e) => { const next = e.target.checked ? [...blockValues, o.value] : blockValues.filter((v) => String(v) !== o.value); if (next.length) onField({ blocks: { ...field.blocks!, [blockMode]: next } }); }} /> {o.label}
                    </label>
                  ))}
                </div>
              ) : (
                <input className="input mono" aria-label="Blocking values" value={toList(blockValues.map(String))} onChange={(e) => { const v = fromList(e.target.value).map(parseScalar); if (v.length) onField({ blocks: { ...field.blocks!, [blockMode]: v } }); }} />
              )}
              <input className="input" aria-label="What happens, for the broker" value={field.blocks.note} onChange={(e) => onField({ blocks: { ...field.blocks!, note: e.target.value } })} />
            </>
          )}
        </div>
      </fieldset>

      <div className="field mt">
        <span className="field-label">On the insurer’s PDF</span>
        {form.pdf ? (
          <PdfTargetEditor target={form.pdf.fields[field.key]} field={field} onChange={onPdf} disabled={off} />
        ) : (
          <span className="hint">Add the insurer’s PDF below to place fields on it.</span>
        )}
        {form.pdf && pdfKind(form.pdf.fields[field.key]) === '' && <span className="hint">Left off the PDF: it goes in the broker’s annex.</span>}
      </div>
    </section>
  ); // prettier-ignore
}

/** The insurer's printed premium table: a premium for each limit in each revenue band. */
function Rating({ form, set, off }: { form: FormDraft; set: (p: Partial<FormDraft>) => void; off: boolean }) {
  const r = form.rating;
  if (!r)
    return (
      <section className="card pad" aria-labelledby="if-rating">
        <h2 id="if-rating" className="section-title row gap"><Table2 size={16} /> Premium table</h2>
        <div className="empty compact">The form prints no premiums: the insurer quotes.</div>
        {!off && <button type="button" className="btn btn-sm mt" onClick={() => set({ rating: { bands: [10_000_000], rows: [{ limit: 1_000_000, deductible: 0, premium: [0] }] } })}><Plus size={14} /> Add a premium table</button>}
      </section>
    ); // prettier-ignore
  const bands = r.bands;
  const extensions = Object.entries(r.extensions ?? {});
  const fit = (ps: number[]) => bands.map((_, i) => ps[i] ?? 0);
  const setRating = (patch: Partial<NonNullable<FormDraft['rating']>>) => set({ rating: { ...r, ...patch } });
  const setBands = (s: string) => {
    const next = fromList(s)
      .map(Number)
      .filter((n) => Number.isFinite(n));
    if (!next.length) return;
    setRating({ bands: next, rows: r.rows.map((row) => ({ ...row, premium: next.map((_, i) => row.premium[i] ?? 0), with: Object.fromEntries(Object.entries(row.with ?? {}).map(([k, ps]) => [k, next.map((_, i) => ps[i] ?? 0)])) })) }); // prettier-ignore
  };
  const setRow = (i: number, patch: Partial<(typeof r.rows)[number]>) =>
    setRating({ rows: r.rows.map((row, n) => (n === i ? { ...row, ...patch } : row)) });
  const money = (n: number) => n.toLocaleString('en-MY');
  return (
    <section className="card pad" aria-labelledby="if-rating">
      <h2 id="if-rating" className="section-title row gap"><Table2 size={16} /> Premium table</h2>
      <fieldset disabled={off} className="plain stack-sm">
        <div className="field">
          <label htmlFor="if-bands">Revenue bands: each band’s upper end in RM, rising</label>
          <input id="if-bands" className="input mono" defaultValue={bands.join(', ')} key={bands.join(',')} onBlur={(e) => setBands(e.target.value)} />
        </div>
        <div className="field">
          <span className="field-label">Optional extensions</span>
          {extensions.map(([k, label], i) => (
            <div key={i} className="ft-row">
              <input className="input mono ft-path" aria-label={`Extension ${i + 1} key`} value={k} onChange={(e) => setRating({ extensions: Object.fromEntries(extensions.map(([a, b], n) => (n === i ? [e.target.value, b] : [a, b]))), rows: r.rows.map((row) => ({ ...row, with: Object.fromEntries(Object.entries(row.with ?? {}).map(([a, ps]) => [a === k ? e.target.value : a, ps])) })) })} />
              <input className="input" aria-label={`Extension ${i + 1} name`} value={label} onChange={(e) => setRating({ extensions: { ...r.extensions, [k]: e.target.value } })} />
              {!off && <button type="button" className="btn btn-icon btn-ghost" aria-label={`Remove extension ${k}`} onClick={() => setRating({ extensions: Object.fromEntries(extensions.filter((_, n) => n !== i)), rows: r.rows.map((row) => ({ ...row, with: Object.fromEntries(Object.entries(row.with ?? {}).filter(([a]) => a !== k)) })) })}><Trash2 size={14} /></button>}
            </div>
          ))}
          {!off && <button type="button" className="btn btn-sm" onClick={() => setRating({ extensions: { ...r.extensions, [`extension_${extensions.length + 1}`]: 'New extension' } })}><Plus size={14} /> Add an extension</button>}
        </div>
        <div className="table-wrap">
          <table className="grid rating-grid" aria-label="Premiums">
            <thead>
              <tr>
                <th>Limit (RM)</th><th>Deductible (RM)</th><th>Cover</th>
                {bands.map((b, i) => <th key={i} className="num">≤ {money(b)}</th>)}
                <th />
              </tr>
            </thead>
            <tbody>
              {r.rows.flatMap((row, i) => [
                <tr key={`${i}-main`}>
                  <td rowSpan={1 + extensions.length}><input className="input num" type="number" aria-label={`Limit ${i + 1}`} value={row.limit} onChange={(e) => setRow(i, { limit: Number(e.target.value) })} /></td>
                  <td rowSpan={1 + extensions.length}><input className="input num" type="number" aria-label={`Deductible ${i + 1}`} value={row.deductible} onChange={(e) => setRow(i, { deductible: Number(e.target.value) })} /></td>
                  <td className="small muted">Main</td>
                  {fit(row.premium).map((p, b) => <td key={b}><input className="input num" type="number" aria-label={`Premium, limit ${row.limit}, band ${b + 1}`} value={p} onChange={(e) => setRow(i, { premium: fit(row.premium).map((x, n) => (n === b ? Number(e.target.value) : x)) })} /></td>)}
                  <td rowSpan={1 + extensions.length}>{!off && r.rows.length > 1 && <button type="button" className="btn btn-icon btn-ghost" aria-label={`Remove limit ${row.limit}`} onClick={() => setRating({ rows: r.rows.filter((_, n) => n !== i) })}><Trash2 size={14} /></button>}</td>
                </tr>,
                ...extensions.map(([k, label]) => {
                  const ps = row.with?.[k];
                  return (
                    <tr key={`${i}-${k}`}>
                      <td className="small muted" title={label}>
                        <label className="toggle-row small"><input type="checkbox" checked={!!ps} onChange={(e) => { const w = { ...(row.with ?? {}) }; if (e.target.checked) w[k] = fit([]); else delete w[k]; setRow(i, { with: w }); }} /> With {k}</label>
                      </td>
                      {bands.map((_, b) => <td key={b}>{ps && <input className="input num" type="number" aria-label={`Premium with ${k}, limit ${row.limit}, band ${b + 1}`} value={ps[b] ?? 0} onChange={(e) => setRow(i, { with: { ...row.with, [k]: fit(ps).map((x, n) => (n === b ? Number(e.target.value) : x)) } })} />}</td>)}
                    </tr>
                  );
                }),
              ])}
            </tbody>
          </table>
        </div>
        {!off && <button type="button" className="btn btn-sm" onClick={() => setRating({ rows: [...r.rows, { limit: (r.rows[r.rows.length - 1]?.limit ?? 0) * 2 || 1_000_000, deductible: r.rows[r.rows.length - 1]?.deductible ?? 0, premium: fit([]) }] })}><Plus size={14} /> Add a limit</button>}
        <div className="field">
          <label htmlFor="if-rating-note">Note shown with a quote</label>
          <input id="if-rating-note" className="input" value={r.note ?? ''} onChange={(e) => setRating({ note: e.target.value })} />
        </div>
        {!off && <button type="button" className="btn btn-sm btn-ghost" onClick={() => set({ rating: undefined })}><Trash2 size={14} /> Remove the premium table</button>}
      </fieldset>
    </section>
  ); // prettier-ignore
}

/** The insurer's own fillable PDF: the edition the fields were mapped on, and its premium grid. */
function Pdf({ form, set, off }: { form: FormDraft; set: (p: Partial<FormDraft>) => void; off: boolean }) {
  const p = form.pdf;
  if (!p)
    return (
      <section className="card pad" aria-labelledby="if-pdf">
        <h2 id="if-pdf" className="section-title row gap"><FileText size={16} /> The insurer’s PDF</h2>
        <div className="empty compact">Not mapped: the submission pack carries the answers in the broker’s annex only.</div>
        {!off && <button type="button" className="btn btn-sm mt" onClick={() => set({ pdf: { sha256: '', pages: 1, fields: {} } })}><Plus size={14} /> Map the insurer’s PDF</button>}
      </section>
    ); // prettier-ignore
  const setPdf = (patch: Partial<NonNullable<FormDraft['pdf']>>) => set({ pdf: { ...p, ...patch } });
  const states = Object.entries(p.quote?.states ?? {});
  return (
    <section className="card pad" aria-labelledby="if-pdf">
      <h2 id="if-pdf" className="section-title row gap"><FileText size={16} /> The insurer’s PDF</h2>
      <p className="muted small">Each field’s place on the PDF is set in the field above. A blank form of another edition is refused on upload.</p>
      <fieldset disabled={off} className="plain stack-sm">
        <div className="two">
          <div className="field">
            <label htmlFor="if-sha">SHA-256 of the blank form</label>
            <input id="if-sha" className="input mono" value={p.sha256} placeholder="64 hex characters" onChange={(e) => setPdf({ sha256: e.target.value.trim().toLowerCase() })} />
            <span className="hint">e.g. <span className="mono">shasum -a 256 form.pdf</span></span>
          </div>
          <div className="field">
            <label htmlFor="if-pages">Pages</label>
            <input id="if-pages" className="input num" type="number" min={1} max={100} value={p.pages} onChange={(e) => setPdf({ pages: Number(e.target.value) })} />
          </div>
        </div>
        <div className="field">
          <span className="field-label">Premium grid (one radio group, a box for each limit in each band)</span>
          {p.quote ? (
            <>
              <input className="input mono" aria-label="Premium grid radio group" value={p.quote.radio} onChange={(e) => setPdf({ quote: { ...p.quote!, radio: e.target.value } })} />
              <span className="hint">Each cell as <span className="mono">extension:limit:band</span> (<span className="mono">main</span> without an extension; bands from 0) → its export state.</span>
              <textarea className="input mono" rows={Math.min(10, states.length + 1)} aria-label="Premium grid states" defaultValue={states.map(([k, v]) => `${k} = ${v}`).join('\n')} onBlur={(e) => setPdf({ quote: { ...p.quote!, states: Object.fromEntries(e.target.value.split('\n').map((l) => l.split('=').map((x) => x.trim())).filter((x) => x.length === 2 && x[0] && x[1]).map(([k, v]) => [k!, v!])) } })} />
              {!off && <button type="button" className="btn btn-sm btn-ghost" onClick={() => setPdf({ quote: undefined })}><Trash2 size={14} /> No premium grid</button>}
            </>
          ) : (
            !off && <button type="button" className="btn btn-sm" onClick={() => setPdf({ quote: { radio: '', states: {} } })}><Plus size={14} /> Add a premium grid</button>
          )}
        </div>
        {!off && <button type="button" className="btn btn-sm btn-ghost" onClick={() => set({ pdf: undefined })}><Trash2 size={14} /> Remove the PDF mapping</button>}
      </fieldset>
    </section>
  ); // prettier-ignore
}
